import { ApiError, createAction } from 'redux-api-middleware';
import { State as StoreState } from '../types/State';
import { HTTP_POST } from './ApiAction';

// Bonus program termination [actions]
export const TERMINATING_REGISTRATION = 'barmer-bonus-shared/registration/TERMINATE' as const;
export const TERMINATED_REGISTRATION = 'barmer-bonus-shared/registration/TERMINATED' as const;
export const TERMINATING_REGISTRATION_FAILED = 'barmer-bonus-shared/registration/TERMINATE_FAIL' as const;
export const DISMISS_TERMINATING_REGISTRATION_FAILED = 'barmer-bonus-shared/registration/TERMINATE_FAIL_DISMISS' as const;
export const DISMISS_PROGRAM_TERMINATED = 'barmer-bonus-shared/registration/DISMISS_PROGRAM_TERMINATED' as const;

export const PROGRAM_BONUS = 'bonus' as const;
export type Program = typeof PROGRAM_BONUS;

type ProgramTerminationAction = {
  type: typeof TERMINATING_REGISTRATION;
  meta: Registration;
  error?: boolean;
  payload?: ApiError;
};
type ProgramTerminatedAction = { type: typeof TERMINATED_REGISTRATION; meta: Registration };
type ProgramTerminationFailedAction = {
  type: typeof TERMINATING_REGISTRATION_FAILED;
  meta: Registration;
  error: boolean;
  payload: ApiError;
};
type ProgramTerminationFailedDismissAction = {
  type: typeof DISMISS_TERMINATING_REGISTRATION_FAILED;
};
type ProgramTerminatedDismissAction = { type: typeof DISMISS_PROGRAM_TERMINATED };

// program: the program to be terminated
// terminated:
//   - true: termination has been successfully completed
//   - false: termination is still on the way
// kvnr: optional insuree number
export type Registration = { program: Program; terminated: boolean; kvnr?: string };

export type Action =
  | ProgramTerminationAction
  | ProgramTerminatedAction
  | ProgramTerminationFailedAction
  | ProgramTerminatedDismissAction
  | ProgramTerminationFailedDismissAction;

export type State = {
  terminating: Registration | null | undefined;
  error?: ApiError;
};

const initialState: State = {
  terminating: null,
  error: undefined,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case TERMINATING_REGISTRATION:
      return {
        ...state,
        terminating: action.meta,
        error: action.error ? action.payload : undefined,
      };
    case TERMINATED_REGISTRATION:
      return {
        ...state,
        terminating: action.meta,
        error: undefined,
      };
    case TERMINATING_REGISTRATION_FAILED:
      return {
        ...state,
        terminating: action.meta,
        error: action.payload,
      };
    case DISMISS_PROGRAM_TERMINATED:
      return {
        ...state,
        terminating: null,
      };
    case DISMISS_TERMINATING_REGISTRATION_FAILED:
      return {
        terminating: null,
        error: undefined,
      };
    default:
      return state;
  }
}

// Whether a(/the specific) program membership is in the process of being terminated
export function isTerminating(globalState: StoreState, program?: Program): boolean {
  return (
    globalState.registration.terminating != null &&
    (program === undefined || program === globalState.registration.terminating.program) &&
    !globalState.registration.terminating.terminated &&
    (globalState.registration.error === undefined || globalState.registration.error === null)
  );
}

// Whether the program membership has been terminated
export function isTerminated(globalState: StoreState, program: Program): boolean {
  return (
    globalState.registration.terminating != null &&
    program === globalState.registration.terminating.program &&
    globalState.registration.terminating.terminated
  );
}

/**
 * Terminate program membership
 *
 * @param program the program we want to terminate
 * @param password user's password to verify whether the request
 * @param kvnr optional, the kvnr insuree number to cancel the program for
 */
export const terminateProgram = (program: Program, password: string, kvnr?: string) =>
  createAction({
    endpoint: `/profile/registrations/${program}/terminate/${kvnr ?? ''}`,
    types: [
      {
        type: TERMINATING_REGISTRATION,
        meta: { program, kvnr, terminated: false },
      },
      {
        type: TERMINATED_REGISTRATION,
        meta: { program, kvnr, terminated: true },
      },
      {
        type: TERMINATING_REGISTRATION_FAILED,
        meta: { program, kvnr, terminated: false },
      },
    ],
    headers: { 'Content-Type': 'application/json' },
    method: HTTP_POST,
    body: JSON.stringify({ password }),
  });

export const dismissError = (): Action => {
  return { type: DISMISS_TERMINATING_REGISTRATION_FAILED };
};

export const dismissProgramTerminated = (): Action => {
  return { type: DISMISS_PROGRAM_TERMINATED };
};
